// @ts-nocheck
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Download, Eye, Edit, Plus } from "lucide-react";
import { supabase } from "@/lib/supabase";

interface MediaKitTemplate {
  id: string;
  template_name: string;
  target_tier: string;
  template_content: {
    title: string;
    description: string;
    placements: string[];
    audience: string;
    reach: string;
  };
  pricing_structure: {
    [key: string]: number;
    package_discount?: number;
  };
  is_active: boolean;
  created_at: string;
}

const MediaKitBuilder: React.FC = () => {
  const [templates, setTemplates] = useState<MediaKitTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMediaKitTemplates();
  }, []);

  const fetchMediaKitTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('media_kit_templates')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching media kit templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'local': return 'bg-blue-100 text-blue-800';
      case 'regional': return 'bg-purple-100 text-purple-800';
      case 'national': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const generateMediaKit = (template: MediaKitTemplate) => {
    // TODO: Implement media kit generation logic
    console.log('Generate media kit for template:', template);
  };

  const previewTemplate = (template: MediaKitTemplate) => {
    // TODO: Implement template preview
    console.log('Preview template:', template);
  };

  const downloadTemplate = (template: MediaKitTemplate) => {
    // TODO: Implement template download
    console.log('Download template:', template);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="h-6 bg-muted rounded"></div>
                <div className="h-4 bg-muted rounded"></div>
                <div className="h-20 bg-muted rounded"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Media Kit Templates</h3>
          <p className="text-sm text-muted-foreground">
            Create and manage media kit templates for different advertiser tiers
          </p>
        </div>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          New Template
        </Button>
      </div>

      {/* Platform Statistics Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Platform Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">125,000+</div>
              <div className="text-sm text-muted-foreground">Monthly Active Users</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-secondary">450,000+</div>
              <div className="text-sm text-muted-foreground">Monthly Page Views</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-accent">15,000+</div>
              <div className="text-sm text-muted-foreground">Athlete Profiles</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">2,500+</div>
              <div className="text-sm text-muted-foreground">Active Coaches</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Media Kit Templates */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {templates.map((template) => (
          <Card key={template.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-4">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{template.template_name}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {template.template_content.description}
                  </p>
                </div>
                <Badge className={getTierColor(template.target_tier)}>
                  {template.target_tier}
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Audience & Reach */}
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="font-medium">Target Audience:</span>
                  <span className="ml-2 text-muted-foreground">
                    {template.template_content.audience}
                  </span>
                </div>
                <div className="text-sm">
                  <span className="font-medium">Reach:</span>
                  <span className="ml-2 text-muted-foreground">
                    {template.template_content.reach}
                  </span>
                </div>
              </div>

              {/* Placements */}
              <div>
                <span className="text-sm font-medium">Available Placements:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {template.template_content.placements.map((placement, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {placement}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Pricing Structure */}
              <div className="bg-muted p-3 rounded-lg">
                <div className="text-sm font-medium mb-2">Pricing Structure:</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(template.pricing_structure).map(([key, value]) => {
                    if (key === 'package_discount') return null;
                    return (
                      <div key={key} className="flex justify-between">
                        <span className="capitalize">{key.replace(/_/g, ' ')}:</span>
                        <span className="font-medium">{formatCurrency(value)}</span>
                      </div>
                    );
                  })}
                  {template.pricing_structure.package_discount && (
                    <div className="col-span-2 mt-1 pt-1 border-t">
                      <div className="flex justify-between text-primary">
                        <span>Package Discount:</span>
                        <span className="font-medium">
                          {(template.pricing_structure.package_discount * 100).toFixed(0)}% off
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button 
                  size="sm" 
                  onClick={() => generateMediaKit(template)}
                  className="gap-1 flex-1"
                >
                  <FileText className="h-3 w-3" />
                  Generate
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => previewTemplate(template)}
                  className="gap-1"
                >
                  <Eye className="h-3 w-3" />
                  Preview
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => downloadTemplate(template)}
                  className="gap-1"
                >
                  <Download className="h-3 w-3" />
                  PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {templates.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Media Kit Templates</h3>
            <p className="text-muted-foreground mb-4">
              Create your first media kit template to get started with advertising sales.
            </p>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Create Template
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MediaKitBuilder;